import { modeDemonstrationActif } from './demo.js';
import demoOpenapi from './demoOpenapi.json';
import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee, dansLaPorteeCourante } from './cles.js';

export interface JetonPresente {
  readonly id: string;
  readonly nom: string;
  readonly prefixe: string;
  readonly scopes: readonly string[];
  readonly etat: 'ACTIF' | 'REVOQUE' | 'EXPIRE';
  readonly expireLe: string | null;
  readonly creeLe: string;
  readonly creePar: string | null;
  readonly dernierUsageLe: string | null;
  readonly revoqueLe: string | null;
}
export interface ReponseJetons {
  readonly jetons: readonly JetonPresente[];
  readonly scopesAccordables: readonly string[];
  readonly durees: readonly number[];
}
export interface CorpsJeton {
  nom: string;
  scopes: string[];
  expireDansJours: 30 | 90 | 180 | 365;
}
export interface ReponseEmission {
  jeton: JetonPresente;
  secret: string;
}
const chemin = (g: string, suite = '') => `/g/${encodeURIComponent(g)}/acces-api${suite}`;
export const accesApiApi = {
  lister: (g: string, appel?: AppelApi) =>
    api.get<ReponseJetons>(chemin(g, '/jetons'), { galerie: g, ...appel }),
  emettre: (g: string, corps: CorpsJeton, appel?: AppelApi) =>
    api.post<ReponseEmission>(chemin(g, '/jetons'), corps, { galerie: g, ...appel }),
  revoquer: (g: string, id: string, appel?: AppelApi) =>
    api.post<{ jeton: JetonPresente }>(
      chemin(g, `/jetons/${encodeURIComponent(id)}/revoquer`),
      {},
      { galerie: g, ...appel },
    ),
  openapi: (g: string, appel?: AppelApi) =>
    modeDemonstrationActif()
      ? Promise.resolve({
          blob: new Blob([JSON.stringify(demoOpenapi, null, 2)], { type: 'application/json' }),
          nomFichier: 'artosera-openapi.json',
        })
      : requeteTelechargement(chemin(g, '/openapi.json'), { galerie: g, ...appel }),
};
export function useJetons(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'acces-api', 'jetons'),
    queryFn: (c) => accesApiApi.lister(g, appelDeRequete(c)),
  });
}
/** Pas de MutationCache : même gcTime:0 retiendrait le secret pendant l'observation. */
export function useEmettreJeton(g: string) {
  const client = useQueryClient();
  const [isPending, setPending] = useState(false);
  const enCours = useRef(false);
  const mutateAsync = async (corps: CorpsJeton): Promise<ReponseEmission> => {
    if (enCours.current) throw new Error('Émission déjà en cours.');
    enCours.current = true;
    setPending(true);
    try {
      const resultat = await dansLaPorteeCourante(async (appel) => {
        const resultat = await accesApiApi.emettre(g, corps, appel);
        return resultat;
      });
      void client.invalidateQueries({ queryKey: cleMetier(g, 'acces-api') });
      return resultat;
    } finally {
      enCours.current = false;
      setPending(false);
    }
  };
  return { isPending, mutateAsync };
}
export function useRevoquerJeton(g: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) => accesApiApi.revoquer(g, id, appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(g, 'acces-api') });
      },
    }),
  );
}
